/**
 * Test Code Encryption
 *
 * XOR-based encryption for protecting PII in generated test files.
 * Encrypts test code before logging/storage to prevent sensitive data exposure.
 */

// ============================================================================
// Configuration
// ============================================================================

const TEST_DATA_KEY = process.env.NEXT_PUBLIC_TEST_DATA_KEY || "";

// ============================================================================
// Encryption Functions
// ============================================================================

/**
 * XOR-based encryption with the env key.
 * Encrypts the entire test file to prevent PII from appearing in logs.
 * Returns base64-encoded encrypted string.
 */
export function encryptTestCode(code: string): string {
  const key = TEST_DATA_KEY;
  // Convert to UTF-8 bytes using TextEncoder
  const encoder = new TextEncoder();
  const codeBytes = encoder.encode(code);

  if (!key) {
    // Fallback to base64 if no key
    return btoa(String.fromCharCode(...codeBytes));
  }

  const keyBytes = Array.from(key).map((c) => c.charCodeAt(0));
  const encrypted = Array.from(codeBytes).map((byte, i) => {
    const keyByte = keyBytes[i % keyBytes.length];
    return String.fromCharCode(byte ^ keyByte);
  });
  return btoa(encrypted.join(""));
}

/**
 * Decrypt XOR-encrypted test code.
 * Inverse of encryptTestCode.
 */
export function decryptTestCode(encrypted: string): string {
  const key = TEST_DATA_KEY;

  // Decode base64
  const decoded = atob(encrypted);
  const decodedBytes = Array.from(decoded).map((c) => c.charCodeAt(0));

  if (!key) {
    // If no key, it was just base64 encoded
    return new TextDecoder().decode(new Uint8Array(decodedBytes));
  }

  const keyBytes = Array.from(key).map((c) => c.charCodeAt(0));
  const decrypted = decodedBytes.map((byte, i) => {
    const keyByte = keyBytes[i % keyBytes.length];
    return byte ^ keyByte;
  });

  return new TextDecoder().decode(new Uint8Array(decrypted));
}
